/**
 * Pure LaTeX style/size transforms for `<math-field>` content.
 *
 * These were inline in `MathEditor.tsx`. They are lifted out for the same
 * reason `fontSizeSteps.ts` was: the *decision* half of a formatting action is
 * ordinary string logic, but the component around it needs a real browser
 * (jsdom renders neither MathLive nor a font size), so logic left inside the
 * component is effectively untestable. Nothing here touches the DOM.
 *
 * They matter more now than when only a focused math-field could be styled:
 * box-level formatting applies a style to EVERY math-field in a text box at
 * once, so a wrong brace match or a missed alias silently corrupts every
 * expression in the box rather than the one the caret was in.
 */
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * LaTeX size commands, smallest to largest. Math has no px sizes — it steps
 * this ladder instead, which is why `larger`/`smaller` need a separate path
 * from the text ladder in `fontSizeSteps.ts`.
 */
export var LATEX_SIZE_COMMANDS = [
    'tiny',
    'scriptsize',
    'footnotesize',
    'small',
    'normalsize',
    'large',
    'Large',
    'LARGE',
    'huge',
    'Huge',
];
/** Index of `\normalsize` — the size an unmarked expression renders at. */
export var NORMALSIZE_INDEX = LATEX_SIZE_COMMANDS.indexOf('normalsize');
/**
 * Remove every `\command{…}` wrapper, keeping the content.
 *
 * Brace *depth* counted rather than a regex, because the content routinely
 * holds braces of its own: `\mathbf{\frac{1}{2}}` must unwrap to `\frac{1}{2}`,
 * and a non-greedy regex would cut at the first `}` and produce `\frac{1`.
 * An unterminated wrapper (`\mathbf{x`) keeps what it has rather than throwing:
 * a half-typed expression must not take down the toolbar.
 */
export function unwrapLatexCommand(command, latex) {
    var prefix = "\\".concat(command, "{");
    var onePass = function (input) {
        var result = '';
        var i = 0;
        while (i < input.length) {
            var idx = input.indexOf(prefix, i);
            if (idx === -1) {
                result += input.slice(i);
                break;
            }
            result += input.slice(i, idx);
            var depth = 1;
            var j = idx + prefix.length;
            while (j < input.length && depth > 0) {
                if (input[j] === '{')
                    depth++;
                else if (input[j] === '}')
                    depth--;
                j++;
            }
            // Unterminated (`\mathbf{x` — a half-typed expression): depth never
            // returned to 0, so there is no closing brace to exclude. Take the rest
            // verbatim; slicing to `j - 1` here would silently swallow the content's
            // last character, and for the shortest case swallow the content entirely.
            result += depth > 0 ? input.slice(idx + prefix.length) : input.slice(idx + prefix.length, j - 1);
            i = j;
        }
        return result;
    };
    // Repeat to a fixed point so a nested `\mathbf{\mathbf{x}}` comes out fully
    // unwrapped. One pass removes only the outer wrapper, which would leave a
    // "remove bold" action returning still-bold latex. Each removing pass makes
    // the string strictly shorter, so this terminates.
    var current = latex;
    for (;;) {
        var next = onePass(current);
        if (next === current)
            return current;
        current = next;
    }
}
export var wrapLatexCommand = function (command, latex) {
    return "\\".concat(command, "{").concat(latex, "}");
};
/**
 * The command(s) that mark each style. Bold has two: MathLive's own toolbar
 * emits `\bm`, this component emits `\mathbf`, and content can arrive from
 * either — so both have to be recognised, or a `\bm`-bolded expression reads as
 * unbolded and a second Bold click double-wraps it instead of clearing it.
 */
var STYLE_COMMANDS = {
    bold: ['mathbf', 'bm'],
    italic: ['mathit'],
    underline: ['underline'],
};
/** Does this latex already carry `style` (under any of its aliases)? */
export function hasLatexStyle(latex, style) {
    return STYLE_COMMANDS[style].some(function (cmd) { return latex.includes("\\".concat(cmd, "{")); });
}
/**
 * Add or remove `style`.
 *
 * `direction` is passed in rather than derived from `latex`, because a box-level
 * action decides ONE direction for the whole box and every math-field must
 * follow it. Deciding per field would leave a box of mixed content toggling
 * some expressions on and others off with a single click.
 *
 * Both paths strip existing wrappers first, so applying a style twice cannot
 * nest (`\mathbf{\mathbf{x}}`) — nesting is invisible on screen but doubles on
 * every click and defeats the next unwrap's alias search.
 */
export function toggleLatexStyle(latex, style, direction) {
    var stripped = STYLE_COMMANDS[style].reduce(function (acc, cmd) { return unwrapLatexCommand(cmd, acc); }, latex);
    if (direction === 'unwrapped')
        return stripped;
    // An empty expression gets nothing: `\mathbf{}` renders as nothing, counts as
    // "already bold" on the next read, and is real content in the save state.
    if (stripped.trim().length === 0)
        return stripped;
    return wrapLatexCommand(STYLE_COMMANDS[style][0], stripped);
}
/**
 * The size rung `latex` currently sits on, plus the latex with that size
 * command removed.
 *
 * Matched longest-name-first: `\LARGE` contains `\large` as a prefix under a
 * case-insensitive read and, more to the point, `indexOf('\\large')` is a
 * substring hit inside `\largesomething`. Testing longer names first stops
 * `\LARGE` being detected as `\large` and stepped to the wrong rung.
 */
export function currentLatexSize(latex) {
    var byLength = __spreadArray([], LATEX_SIZE_COMMANDS, true).sort(function (a, b) { return b.length - a.length; });
    for (var _i = 0, byLength_1 = byLength; _i < byLength_1.length; _i++) {
        var cmd = byLength_1[_i];
        if (!latex.includes("\\".concat(cmd)))
            continue;
        var index = LATEX_SIZE_COMMANDS.indexOf(cmd);
        // A size command appears either as a wrapper (`\large{x}`) or as a bare
        // switch that applies to the rest of the group (`\large x`). Strip both.
        var stripped = unwrapLatexCommand(cmd, latex).split("\\".concat(cmd)).join('');
        return { index: index, stripped: stripped };
    }
    return { index: NORMALSIZE_INDEX, stripped: latex };
}
/**
 * Step `latex` one rung up or down the size ladder.
 *
 * Clamps at both ends rather than wrapping around, so holding Larger on an
 * already-`\Huge` expression is a no-op instead of snapping it back to `\tiny`.
 * `\normalsize` is written out explicitly when the step lands there — leaving it
 * off would be equivalent on screen, but the explicit command is what makes a
 * later `currentLatexSize` read the rung back instead of guessing the default.
 */
export function stepLatexSize(latex, direction) {
    var _a = currentLatexSize(latex), index = _a.index, stripped = _a.stripped;
    if (stripped.trim().length === 0)
        return stripped;
    var next = direction === 'larger'
        ? Math.min(index + 1, LATEX_SIZE_COMMANDS.length - 1)
        : Math.max(index - 1, 0);
    if (next === index)
        return latex;
    return wrapLatexCommand(LATEX_SIZE_COMMANDS[next], stripped);
}
